import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Variables } from '../variables';

export type ButtonGroupVariant = 'segmented' | 'pills' | 'track';

interface IButtonProps {
    selected?: boolean;
}

const Track = css`
    height: 30px;
    padding: 2px;
    box-sizing: border-box;
    border: 1px solid ${Variables.Swatches.Gray3};
    border-radius: 6px;
    background-color: ${Variables.Swatches.Gray2};
`;

export const Container = styled.div<{ variant?: ButtonGroupVariant }>`
    display: flex;
    align-items: center;
    gap: ${(props): string => (props.variant === 'pills' ? '6px' : props.variant === 'track' ? '2px' : '0')};
    ${(props): ReturnType<typeof css> | string => (props.variant === 'track' ? Track : '')}
`;

const UnselectedButton = css`
    cursor: pointer;

    font-weight: 500;
    color: ${Variables.Colors.n50};
    background-color: ${Variables.Colors.white};

    &:hover {
        background-color: ${Variables.Colors.n15_24};
    }

    &:active,
    &:focus {
        color: ${Variables.Colors.n100};
        background-color: ${Variables.Colors.n15_48};
        border: ${Variables.Borders.regular};
        &:not(:last-child) {
            border-right: none;
        }
    }
    &:disabled {
        color: ${Variables.Colors.n15};
        &:hover {
            background-color: ${Variables.Colors.white};
        }
    }
`;
const SelectedButton = css`
    font-weight: 500;
    color: ${Variables.Colors.n100};
    background-color: ${Variables.Colors.n15_32};
    &:disabled {
        color: ${Variables.Colors.n15};
    }
`;
export const Button = styled.button`
    display: flex;
    flex: 1 1;
    justify-content: center;

    font-size: 15px;
    padding: 6px 20px;

    user-select: none;
    outline: none;

    border: 1px solid ${Variables.Colors.n15};

    &:first-of-type {
        border-top-left-radius: ${Variables.Rounded.small};
        border-bottom-left-radius: ${Variables.Rounded.small};
    }
    &:last-child {
        border-top-right-radius: ${Variables.Rounded.small};
        border-bottom-right-radius: ${Variables.Rounded.small};
    }
    &:not(:last-child) {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        border-right: none;
    }
    &:not(:first-of-type) {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }

    ${(props: IButtonProps) => (props.selected ? SelectedButton : UnselectedButton)}

    transition: all 0.1s linear;
`;

const SelectedPill = css`
    color: ${Variables.Swatches.Blue8};
    background-color: ${Variables.Swatches.AlphaMiddleBlue10};
    border-color: ${Variables.Swatches.Blue6};
`;

const UnselectedPill = css`
    color: ${Variables.Swatches.Gray7};
    background-color: ${Variables.Colors.white};
    border-color: ${Variables.Swatches.Gray3};

    &:hover:not(:disabled) {
        background-color: ${Variables.Swatches.Gray1};
    }
    &:disabled {
        color: ${Variables.Swatches.Gray4};
        cursor: default;
    }
`;

/**
 * A `pills` item: separate, rounded, 30px high. The selected one is outlined and tinted rather than filled, so it
 * reads as the current view and not as a call to action next to the page's primary button.
 */
export const Pill = styled.button`
    display: inline-flex;
    align-items: center;
    height: 30px;
    padding: 0 10px;
    border: 1px solid;
    border-radius: 6px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    outline: none;
    transition: all 0.1s linear;

    &:focus-visible {
        box-shadow: 0 0 0 2px ${Variables.Swatches.AlphaMiddleBlue30};
    }

    ${(props: IButtonProps) => (props.selected ? SelectedPill : UnselectedPill)}
`;

const SelectedTrackItem = css`
    color: ${Variables.Swatches.Gray11};
    background-color: ${Variables.Colors.white};
    box-shadow: ${Variables.Shadows.border};
`;

/**
 * A `track` item: a segment inside one grey track, the selected one lifted onto white — the date range control above
 * the denials table. Quieter than `pills`, because it scopes the data rather than choosing a view.
 */
export const TrackItem = styled.button`
    display: inline-flex;
    align-items: center;
    height: 24px;
    padding: 0 10px;
    border: none;
    border-radius: 4px;
    background-color: transparent;
    color: ${Variables.Swatches.Gray7};
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    outline: none;
    transition: all 0.1s linear;

    &:hover:not(:disabled) {
        color: ${Variables.Swatches.Gray11};
    }
    &:focus-visible {
        box-shadow: 0 0 0 2px ${Variables.Swatches.AlphaMiddleBlue30};
    }
    &:disabled {
        color: ${Variables.Swatches.Gray4};
        cursor: default;
    }

    ${(props: IButtonProps) => (props.selected ? SelectedTrackItem : '')}
`;

/** Carries an item's `title`, which its button cannot show while disabled. */
export const ItemSlot = styled.span`
    display: inline-flex;
`;
